import React, { useEffect, useState } from 'react';
import styledImport, { css } from 'styled-components';
import type { ThemedStyledInterface } from 'styled-components';

import type { NavbarTheme } from '../state/Navbar.theme';

const styled = styledImport as ThemedStyledInterface<NavbarTheme>;

import ProfileImage from '../../../components/ProfileImage';
import windowService from '../../../services/windowService';
import type { NavbarAction } from '../hooks/useNavbar';
import { Breakpoints, Color } from '../../../app/StyleGuide';
import { AuthPillButton } from './AuthPillButton';

const MobileMenuToggleBar = styled.div`
  display: flex;
  align-items: stretch;
  margin-left: -15px;
  margin-right: 0;
  min-height: 60px;
  position: relative;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: none;
  }
`;

const MobileMenuToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  border: none;
  padding: 16px;
  background: transparent;
  color: ${({ theme }) => theme.mobileIconColor};
  cursor: pointer;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -6px;
  }

  i {
    font-size: 20px;
  }
`;

const MobileMenuHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 8px 0;
  padding-top: calc(8px + env(safe-area-inset-top, 0px));
`;

const MobileMenuCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 48px;
  padding: 12px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.mobileIconColor};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -6px;
  }

  i {
    font-size: 24px;
  }
`;

const MobileMenuBackdrop = styled.div<{ $open: boolean }>`
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  border: none;
  padding: 0;
  margin: 0;
  background: rgba(0, 0, 0, 0.45);
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease, visibility 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: none;
  }
`;

const MobileMenuPanel = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 85%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 0;
  height: auto;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.textColor};
  box-shadow: ${({ $open }) => ($open ? '2px 0 12px rgba(0, 0, 0, 0.25)' : 'none')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
  transition: opacity 0.2s ease, transform 0.25s ease, visibility 0.25s ease;
  overflow: hidden;
  z-index: 1000;

  @media (min-width: ${Breakpoints.screen_sm}) {
    display: none;
  }
`;

const MobileMenuScroll = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 0 24px;
`;

const MobileMenuSection = styled.div<{ $borderTop?: boolean }>`
  ${({ $borderTop, theme }) =>
    $borderTop &&
    css`
      border-top: 1px solid ${theme.headerBorderColor};
    `}
`;

const MobileMenuAction = styled.button<{ $active?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  border: none;
  background: ${({ $active, theme }) => ($active ? theme.activeBackground : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.activeTextColor : theme.textColor)};
  font-size: 16px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.125s linear, color 0.125s linear;

  &:hover,
  &:focus {
    background: ${({ $active, theme }) => ($active ? theme.activeBackground : theme.hoverBackground)};
  }

  i {
    width: 18px;
    text-align: center;
    color: ${({ $active, theme }) => ($active ? theme.activeIconColor : theme.navIconColor)};
    opacity: ${({ $active, theme }) => ($active ? theme.activeIconOpacity : theme.navIconOpacity)};
    transition: color 0.125s linear, opacity 0.125s linear;
  }
`;

const MobileMenuFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.headerBorderColor};
  background: rgba(0, 0, 0, 0.12);
  padding-bottom: env(safe-area-inset-bottom, 0);
`;

const MobileProfileButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textColor};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.hoverBackground};
  }
`;

const MobileProfileImageThumbnail = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 40px;
  color: ${Color.Gray._30};
  background-color: ${Color.white};
  overflow: hidden;
  border-radius: 999px;
  flex-shrink: 0;
`;

const MobileProfileText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const MobileProfileTitle = styled.span`
  margin-top: 2px;
  font-size: 16px;
  font-weight: 600;
  text-transform: ${({ theme }) => theme.dashboardNameTransform};
`;

const MobileLoggedOutActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px 24px;
`;

const MobileRegisterButton = styled(AuthPillButton).attrs({ $variant: 'secondary' as const })`
  flex: 1 1 0;
  padding: 10px 16px;
  font-size: 14px;
`;

const MobilePanelLoginButton = styled(AuthPillButton).attrs({ $variant: 'primary' as const })`
  flex: 1 1 0;
  padding: 10px 16px;
  font-size: 14px;
`;

export type NavbarMobileAction = {
  key: string;
  label: string;
  iconClass: string;
  qaHook: string;
  active?: boolean;
  onSelect: () => void;
};

type NavbarMobileMenuProps = {
  loggedIn: boolean;
  primaryActions: NavbarMobileAction[];
  moreActions: NavbarAction[];
  loggedOutActions: NavbarMobileAction[];
  dashboardLabel: string;
  profileImageUrl?: string;
  onLogin: () => void;
  onRegister: () => void;
};

export default function NavbarMobileMenu({
  loggedIn,
  primaryActions,
  moreActions,
  loggedOutActions,
  dashboardLabel,
  profileImageUrl,
  onLogin,
  onRegister,
}: NavbarMobileMenuProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleSelectAction = (onSelect: () => void) => {
    setIsMobileMenuOpen(false);
    onSelect();
  };

  return (
    <>
      <MobileMenuToggleBar>
        <MobileMenuToggle
          type="button"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navbar-menu"
          aria-haspopup="true"
          aria-label="Open menu"
          data-qa-hook="mobileMenuToggle"
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <i className="fas fa-bars" />
        </MobileMenuToggle>
      </MobileMenuToggleBar>

      <>
        <MobileMenuBackdrop
          aria-hidden={false}
          data-qa-hook="mobileMenuBackdrop"
          $open={isMobileMenuOpen}
          onClick={() => setIsMobileMenuOpen(false)}
        />

        <MobileMenuPanel
          id="mobile-navbar-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
          data-qa-hook="mobileMenuPanel"
          $open={isMobileMenuOpen}
        >
          <MobileMenuHeader>
            <MobileMenuCloseButton
              type="button"
              aria-label="Close menu"
              data-qa-hook="mobileMenuClose"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="fas fa-times" aria-hidden />
            </MobileMenuCloseButton>
          </MobileMenuHeader>
          <MobileMenuScroll>
            {isMobileMenuOpen && (
              <MobileMenuSection>
                {(loggedIn ? primaryActions : loggedOutActions).map(action => (
                  <MobileMenuAction
                    key={action.key}
                    type="button"
                    data-qa-hook={`mobile-${action.qaHook}`}
                    $active={action.active}
                    onClick={() => handleSelectAction(action.onSelect)}
                  >
                    <i className={action.iconClass} />
                    <span>{action.label}</span>
                  </MobileMenuAction>
                ))}
              </MobileMenuSection>
            )}
          </MobileMenuScroll>
          {isMobileMenuOpen &&
            (loggedIn ? (
              <MobileMenuFooter>
                <MobileProfileButton
                  type="button"
                  data-qa-hook="mobileMenuDashboard"
                  onClick={() => windowService.redirectTo('/dashboard')}
                >
                  <MobileProfileImageThumbnail>
                    <ProfileImage src={profileImageUrl} size="40px" />
                  </MobileProfileImageThumbnail>
                  <MobileProfileText>
                    <MobileProfileTitle>{dashboardLabel}</MobileProfileTitle>
                  </MobileProfileText>
                </MobileProfileButton>

                <MobileMenuSection $borderTop>
                  {moreActions.map(action => (
                    <MobileMenuAction
                      key={action.key}
                      type="button"
                      data-qa-hook={action.qaHook}
                      onClick={() => handleSelectAction(action.onSelect)}
                    >
                      <i className={action.iconClass} />
                      <span>{action.label}</span>
                    </MobileMenuAction>
                  ))}
                </MobileMenuSection>
              </MobileMenuFooter>
            ) : (
              <MobileMenuFooter>
                <MobileLoggedOutActions>
                  <MobileRegisterButton type="button" onClick={() => handleSelectAction(onRegister)}>
                    Register
                  </MobileRegisterButton>
                  <MobilePanelLoginButton type="button" onClick={() => handleSelectAction(onLogin)}>
                    Log In
                  </MobilePanelLoginButton>
                </MobileLoggedOutActions>
              </MobileMenuFooter>
            ))}
        </MobileMenuPanel>
      </>
    </>
  );
}
